import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { EASE_OUT_NOTICE } from "@/lib/motion";
import { X } from "lucide-react";
import { useTranslation } from "@/i18n/I18nProvider";
import { readConsent } from "@/lib/anonId";
import { useAuthStore } from "@/services/authStore";

/**
 * Guest sign-up nudge — a small bottom card (ConsentNotice's shell) shown to
 * guests once per browser session ("opened the app again" semantics: the
 * sessionStorage flag dies when the tab/app fully closes). Honest benefits
 * only: the things an account actually gates today (live capacity, the Offers
 * tab) plus saved visits & feedback — and it's free.
 *
 * Sequencing: renders at 2600ms — after ConsentNotice (1200ms), before
 * RatingPrompt (4000ms). On a first-ever visit the consent card owns the
 * bottom corner, so the nudge silently defers to the NEXT session (the
 * session flag is deliberately not written). Never shown while signed in;
 * signing up/in dismisses it instantly.
 */

const SESSION_KEY = "resto_signup_nudge";
const SHOW_DELAY_MS = 2600;

function sessionSeen(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    return false; // private mode / storage off — nudge just stays re-eligible
  }
}

function markSeen() {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    /* see above */
  }
}

export interface SignupNudgeProps {
  /** Deep-links into the account sidebar's sign-up view. */
  onSignUp: () => void;
}

export function SignupNudge({ onSignUp }: SignupNudgeProps) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const status = useAuthStore((s) => s.status);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (status !== "signed_out") {
      // Covers both "signed in mid-nudge" (hide instantly) and the boot
      // "initializing" window (never fire before auth resolves).
      setVisible(false);
      return;
    }
    if (sessionSeen()) return;
    const timer = window.setTimeout(() => {
      if (useAuthStore.getState().status !== "signed_out") return;
      if (readConsent() === "unset") return; // consent card is showing — defer
      markSeen(); // shown once per session, even if dismissed immediately
      setVisible(true);
    }, SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [status]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.aside
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
          animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={EASE_OUT_NOTICE}
          role="region"
          aria-label={t("SIGNUP_NUDGE_TITLE")}
          className="fixed inset-x-3 z-[80] rounded-2xl border border-white/15 light:border-black/15 bg-zinc-950/95 light:bg-white/95 p-4 shadow-[0_0_0_1px_#000,0_16px_40px_-20px_#000] backdrop-blur sm:inset-x-auto sm:right-5 sm:max-w-sm"
          style={{ bottom: "max(0.75rem, env(safe-area-inset-bottom, 0px))" }}
        >
          <div className="flex items-start justify-between gap-3">
            <h2 className="font-display text-[15px] tracking-tight text-zinc-50 light:text-zinc-900">
              {t("SIGNUP_NUDGE_TITLE")}
            </h2>
            <button
              type="button"
              onClick={() => setVisible(false)}
              aria-label={t("CLOSE")}
              className="-mr-1 -mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-dim light:text-zinc-600 transition hover:text-zinc-200 light:hover:text-zinc-800"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <p className="mt-1 text-pretty text-[13px] leading-relaxed text-zinc-300 light:text-zinc-700">
            {t("SIGNUP_NUDGE_BODY")}
          </p>
          <button
            type="button"
            onClick={() => {
              setVisible(false);
              onSignUp();
            }}
            className="mt-3 inline-flex items-center justify-center rounded-full bg-paper light:bg-ink px-4 py-1.5 text-xs font-semibold text-ink light:text-paper transition hover:bg-white light:hover:bg-zinc-800"
          >
            {t("SIGNUP_FREE_CTA")}
          </button>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
